/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import { DS, DSErrors, DSUtils } from 'js-data';
import differenceBy from 'lodash/differenceBy';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';

import analyticsCounterResource from '../analytics/analytics-counter-resource';
import analyticsGroupResource from '../analytics/analytics-group-resource';
import assetResource from '../asset/asset-resource';
import contentTaskResource from '../content/task/content-task-resource';
import exceptionResource from '../exception/exception-resource';
import layerResource from '../layer/layer-resource';
import layoutResource from '../layout/layout-resource';
import mediumResource from '../medium/medium-resource';
import playlistResource from '../playlist/playlist-resource';
import powerTaskResource from '../power/task/power-task-resource';
import sessionResource from '../session/session-resource';
import taskResource from '../task/task-resource';
import timerResource from '../timer/timer-resource';
import trackResource from '../track/track-resource';

// TODO #471 Doc: Aktualizovat store diagram (Asset#checksum)
/**
 * Datový sklad postavený na knihovně JSData.
 *
 * ![Diagram tříd](./core/store/store.doc.png)
 * @extends DS
 */
class Store extends DS {
  /**
   * Vytvoří datový sklad.
   * @param {Object.<string, DsAdapter>|null} dsAdapterMap Mapa adaptérů pro JSData.
   * @returns {Store} Nový datový sklad.
   */
  constructor(dsAdapterMap) {
    super({
      // Simple where může způsobit nechtěné operace např. {limit: 1} má filtrovat, nebo stránkovat?
      allowSimpleWhere: false,
      // Načítání z cache pouze na vyžádání. Může způsobit nepříliš očekávané chování.
      bypassCache: true,
      defaultAdapter: 'storage',
      // Optimalizace výkonu. Naslouchat na změny u datových zdrojů.
      instanceEvents: false,
      // Optimalizace výkonu. Deprecated computed properties. Používat raději getter / setter styl.
      watchChanges: false
    });
    // Registrace adaptérů
    if (isObject(dsAdapterMap)) {
      Object.entries(dsAdapterMap).forEach(([key, dsAdapter]) => {
        this.registerAdapter(key, dsAdapter);
      });
    }
    // Registrace datových zdrojů
    this.defineResource(analyticsCounterResource);
    this.defineResource(analyticsGroupResource);
    this.defineResource(assetResource);
    this.defineResource(contentTaskResource);
    this.defineResource(exceptionResource);
    this.defineResource(layerResource);
    this.defineResource(layoutResource);
    this.defineResource(mediumResource);
    this.defineResource(playlistResource);
    this.defineResource(powerTaskResource);
    this.defineResource(sessionResource);
    this.defineResource(taskResource);
    this.defineResource(timerResource);
    this.defineResource(trackResource);
  }

  /**
   * Vytvoření více objektů persistentně zároveň.
   * Slouží především k optimalizaci výkonu: omezení emitovaných událostí a operací s adaptérem.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsAttrs>} attrsArray Objekty ke vložení v poli.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Promise.<Array.<DsModel>>} Odebrané modely.
   */
  createMany(resourceName, attrsArray, options) {
    // TODO Hotfix implementace. Využívat adapter.createMany a inject(attrsArray).
    // - https://github.com/js-data/js-data/blob/2.10.0/src/datastore/async_methods/create.js
    return Promise.all(
      attrsArray.map((attrs) => this.create(resourceName, attrs, options))
    );
  }

  /**
   * Odebrání více objektů persistentně zároveň.
   * Slouží především k optimalizaci výkonu: omezení emitovaných událostí a operací s adaptérem.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsId>} idArray Identifikátory k odebrání v poli.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Promise.<Array.<DsModel>>} Odebrané modely.
   */
  destroyMany(resourceName, idArray, options) {
    // TODO Hotfix implementace. Využívat adapter.destroyMany a ejectMany(idArray).
    // - https://github.com/js-data/js-data/blob/2.10.0/src/datastore/async_methods/destroy.js
    // - https://github.com/js-data/js-data/blob/2.10.0/src/datastore/async_methods/destroyAll.js
    return Promise.all(
      idArray.map((id) => this.destroy(resourceName, id, options))
    );
  }

  /**
   * Odebrání více objektů v paměti zároveň.
   * Slouží především k optimalizaci výkonu: omezení emitovaných událostí a operací s adaptérem.
   * Do budoucna by nemělo využívat eject, ale maximálně optimalizovat výkon hromadným odebráním.
   * Podporuje pouze notify a ne už přímé volání definition.beforeEject + definition.afterEject.
   * Ideálně by asi mělo být přímo součástí eject, stejně jako inject, který podporuje vkládání více objektů.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsId>} idArray Identifikátory k odebrání v poli.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Array.<DsModel>} Odebrané modely.
   */
  ejectMany(resourceName, idArray, options) {
    const definition = this.definitions[resourceName];
    if (!definition) {
      throw new DSErrors.NER(resourceName);
    }
    if (!isArray(idArray)) {
      throw new DSErrors.IA(
        `${resourceName}.ejectMany: "idArray" must be an array!`
      );
    }
    // Nalezení příslušných modelů, aby šlo předem určit, jestli emitovat beforeEject.
    const modelArray = idArray
      .map((id) => this.get(resourceName, id))
      .filter((model) => model);
    // Bez nalezených modelů se žádné operace neprovádějí.
    if (!modelArray.length) {
      return [];
    }
    const { idAttribute } = definition;
    // Spojení nastavení ze vstupu a z resource.
    const inheritedOptions = DSUtils._(definition, options);
    // Vyvolání lifecycle události pro všechny modely.
    if (inheritedOptions.notify) {
      definition.emit('DS.beforeEject', definition, modelArray);
    }
    // Odebrání všech modelů pomocí eject bez vyvolání událostí.
    const ejectOptions = { ...options, notify: false };
    const ejectedModels = modelArray.map((model) =>
      this.eject(resourceName, model[idAttribute], ejectOptions)
    );
    // Vyvolání lifecycle události pro všechny modely.
    if (inheritedOptions.notify) {
      definition.emit('DS.afterEject', definition, modelArray);
    }
    return ejectedModels;
  }

  /**
   * Nahradí obsah datového zdroje v paměti za novou sadu objektů.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsAttrs>} attrsArray Objekty ke vložení v poli.
   * @param {DsParams|null} params Filtrovací kritéria JSData.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Array.<DsModel>} Nově vložené modely.
   */
  replace(resourceName, attrsArray, params, options) {
    let result;
    const definition = this.definitions[resourceName];
    if (!definition) {
      throw new DSErrors.NER(resourceName);
    }
    if (!isArray(attrsArray)) {
      throw new DSErrors.IA(
        `${resourceName}.replace: "attrs" must be an array!`
      );
    }
    const { idAttribute } = definition;
    const currentModelArray = this.filter(resourceName, params, options);
    const ejectModelArray = differenceBy(
      currentModelArray,
      attrsArray,
      idAttribute
    );
    // Odebrání všech ostatních modelů.
    if (ejectModelArray.length) {
      const ejectIdArray = ejectModelArray.map((model) => model[idAttribute]);
      this.ejectMany(resourceName, ejectIdArray, options);
    }
    // Přidání nových modelů.
    if (attrsArray.length) {
      result = this.inject(resourceName, attrsArray, options);
    } else {
      result = [];
    }
    return result;
  }

  /**
   * Nahradí obsah datového zdroje persistentně za novou sadu objektů.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsAttrs>} attrsArray Objekty ke vložení v poli.
   * @param {DsParams|null} params Filtrovací kritéria JSData.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Promise.<Array.<DsModel>>} Nově vložené modely.
   */
  replaceAsync(resourceName, attrsArray, params, options) {
    const definition = this.definitions[resourceName];
    if (!definition) {
      throw new DSErrors.NER(resourceName);
    }
    if (!isArray(attrsArray)) {
      throw new DSErrors.IA(
        `${resourceName}.replaceAsync: "attrs" must be an array!`
      );
    }
    const { idAttribute } = definition;
    return this.findAll(resourceName, params, options)
      .then((currentModelArray) => {
        let result;
        const ejectModelArray = differenceBy(
          currentModelArray,
          attrsArray,
          idAttribute
        );
        // Odebrání všech ostatních modelů.
        if (ejectModelArray.length) {
          const ejectIdArray = ejectModelArray.map(
            (model) => model[idAttribute]
          );
          result = this.destroyMany(resourceName, ejectIdArray, options);
        }
        return result;
      })
      .then(() => {
        let result;
        // Přidání nových modelů.
        if (attrsArray.length) {
          result = this.createMany(resourceName, attrsArray, options);
        } else {
          result = [];
        }
        return result;
      });
  }
}

export default Store;
